import { useEffect, useState } from "react";

import firebase from "../../firebase";
import { getNotificationVisibleTimestamp } from "../home/notificationTiming";
import { licenceIsCurrent } from "./desktopAccess";

const CACHE_PREFIX = "desktopLicence:";
const CHANGED_EVENT = "desktopLicenceChanged";

// Several parts of a page can ask at once on first load; they share one
// request per user rather than making several.
const inFlight = new Map();

function readCache(uid) {
    try {
        const raw = sessionStorage.getItem(CACHE_PREFIX + uid);
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        return null;
    }
}

function writeCache(uid, licence) {
    try {
        sessionStorage.setItem(
            CACHE_PREFIX + uid,
            JSON.stringify({ checkedAt: Date.now(), licence: licence }),
        );
    } catch (error) {
        // Private windows can refuse storage; the next render just asks again
    }
}

/**
 * Records a licence the page has just fetched (the Desktop App page does this),
 * so the menu shortcut appears or disappears at once rather than next session.
 */
export function rememberDesktopLicence(uid, licence) {
    if (!uid) return;
    writeCache(uid, licence || null);
    window.dispatchEvent(
        new CustomEvent(CHANGED_EVENT, { detail: { uid: uid, licence: licence || null } }),
    );
}

/** When the newest Nodiant Coder notification for this user arrived (ms), or 0. */
export function latestDesktopAppNotificationTime(notifications) {
    return (notifications || []).reduce(
        (latest, notification) =>
            notification.type === "desktopApp"
                ? Math.max(latest, getNotificationVisibleTimestamp(notification))
                : latest,
        0,
    );
}

function fetchLicence(uid) {
    if (!inFlight.has(uid)) {
        const request = (async () => {
            const result = await firebase
                .functions()
                .httpsCallable("getMyDesktopLicence")();
            const found = (result.data && result.data.licence) || null;
            writeCache(uid, found);
            return found;
        })().finally(() => inFlight.delete(uid));

        inFlight.set(uid, request);
    }

    return inFlight.get(uid);
}

/**
 * Whether the signed-in user holds a Nodiant Coder key that hasn't expired:
 * decides the Desktop App shortcut in the menu and whether Nodiant Coder
 * notifications show in the new-notifications popup.
 *
 * Keys live in Supabase, so this asks getMyDesktopLicence once per browser
 * session and caches the answer in sessionStorage. It asks again when a
 * Nodiant Coder notification newer than the cached answer arrives (a grant,
 * renewal or extra Macs), and opening the Desktop App page refreshes it too.
 * Any failure counts as "no key": the shortcut and the notifications stay
 * hidden until a check succeeds.
 */
export default function useHasCurrentDesktopLicence(uid, notifications) {
    const notifiedAt = latestDesktopAppNotificationTime(notifications);
    const [licence, setLicence] = useState(() => {
        const cached = uid ? readCache(uid) : null;
        return cached ? cached.licence : null;
    });

    useEffect(() => {
        if (!uid) {
            setLicence(null);
            return undefined;
        }

        let cancelled = false;

        function handleChanged(event) {
            if (event.detail && event.detail.uid === uid) {
                setLicence(event.detail.licence);
            }
        }
        window.addEventListener(CHANGED_EVENT, handleChanged);

        const cached = readCache(uid);
        if (cached && cached.checkedAt >= notifiedAt) {
            setLicence(cached.licence);
        } else {
            fetchLicence(uid).then(
                (found) => {
                    if (!cancelled) setLicence(found);
                },
                () => {
                    if (!cancelled) setLicence(null);
                },
            );
        }

        return () => {
            cancelled = true;
            window.removeEventListener(CHANGED_EVENT, handleChanged);
        };
    }, [uid, notifiedAt]);

    return licenceIsCurrent(licence);
}
